/**
 * Rollout Configuration — Phased Feature Rollout
 *
 * Controls which features are available in each phase.
 * Phase-based access is an ADDITIONAL constraint layered on top of role and
 * plan-based access — a feature must pass all three gates to be visible.
 *
 * ─── Phase definitions ────────────────────────────────────────────────────────
 *  PHASE_0  Founding Agent Core     Minimum viable feature set at launch
 *  PHASE_1  Power Agent / CRM Depth Enhanced agent workflow features
 *  PHASE_2  Agency Owner Features   Full agency admin capabilities
 *
 * ─── Controlling the active phase ────────────────────────────────────────────
 *  Now:    Set VITE_ROLLOUT_PHASE in your .env / AWS SSM Parameter Store.
 *          Changing the env var and redeploying unlocks the phase for all agencies.
 *
 *  Future: Per-agency override via user.agencyPhase returned by /api/me.
 *          Pass it as the second argument to isPhaseActive() / isRouteInPhase().
 *          The global env var becomes the default; agencies can be individually
 *          advanced or held back without a deploy.
 *
 * ─── IMPORTANT ───────────────────────────────────────────────────────────────
 *  Phase gates are a UI/UX constraint, NOT a security boundary.
 *  Backend routes enforce their own authorization independently.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// Global active phase — change VITE_ROLLOUT_PHASE env var to advance rollout.
// Defaults to PHASE_0 (safest option — nothing unexpected unlocked on bad config).
export const ACTIVE_PHASE = import.meta.env.VITE_ROLLOUT_PHASE || 'PHASE_0';

// Numeric levels for comparison — higher = more features unlocked
const PHASE_HIERARCHY = {
  PHASE_0: 0,
  PHASE_1: 1,
  PHASE_2: 2,
};

/**
 * Check if a phase is active or has been reached.
 *
 * @param {string}           requiredPhase  Phase that must be active ('PHASE_0' | 'PHASE_1' | 'PHASE_2')
 * @param {string|undefined} agencyPhase    Optional per-agency phase override from /api/me.
 *                                          When provided, takes precedence over the global env var.
 *                                          Use this when per-agency rollout is supported by the backend.
 * @returns {boolean}
 */
export const isPhaseActive = (requiredPhase, agencyPhase) => {
  const effectivePhase = agencyPhase ?? ACTIVE_PHASE;
  const activeLevel = PHASE_HIERARCHY[effectivePhase] ?? 0;
  const requiredLevel = PHASE_HIERARCHY[requiredPhase] ?? 0;
  return activeLevel >= requiredLevel;
};

/**
 * Feature-to-phase mapping.
 * Maps routes/features to their required phase.
 */
export const FEATURE_PHASES = {
  // ── PHASE_0: Founding Agent Core ─────────────────────────────────────────
  '/agent/dashboard':               'PHASE_0',
  '/agent/itinerary/view/:id':      'PHASE_0',
  '/agent/crm/clients':             'PHASE_0',
  '/agent/crm/clients/:id':         'PHASE_0',
  '/agent/crm/clients/new':         'PHASE_0',
  '/agent/crm/trips':               'PHASE_0',
  '/agent/crm/trips/:id':           'PHASE_0',
  '/questionnaire/:tripId':         'PHASE_0',
  '/agent/documents':               'PHASE_0',
  '/agent/settings':                'PHASE_0',
  '/agent/notifications':           'PHASE_0',
  '/forgot-password':               'PHASE_0',
  '/reset-password':                'PHASE_0',

  // Client portal — Phase 0
  '/i/:tripId':                     'PHASE_0',
  '/i/:tripId/itinerary':           'PHASE_0',
  '/i/:tripId/docs':                'PHASE_0',
  '/i/:tripId/messages':            'PHASE_0',

  // Destination trip management — Phase 0
  '/agent/crm/disney-world':        'PHASE_0',
  '/agent/crm/disneyland':          'PHASE_0',
  '/agent/crm/universal-orlando':   'PHASE_0',
  '/agent/crm/disney-cruise':       'PHASE_0',
  '/agent/disney-world':            'PHASE_0',
  '/agent/disneyland':              'PHASE_0',
  '/agent/universal-orlando':       'PHASE_0',
  '/agent/disney-cruise':           'PHASE_0',

  // ── PHASE_1: Power Agent / CRM Depth ─────────────────────────────────────
  '/agent/crm':                             'PHASE_1',
  '/agent/crm/leads':                       'PHASE_1',
  '/agent/crm/leads/:id':                   'PHASE_1',
  '/agent/crm/commissions':                 'PHASE_1',
  '/agent/crm/commissions/:id':             'PHASE_1',
  '/agent/crm/pipeline':                    'PHASE_1',
  '/agent/crm/communications':              'PHASE_1',
  '/agent/crm/subscriptions':               'PHASE_1',
  '/agent/sales-pipeline':                  'PHASE_1',

  // Client portal — Phase 1 features
  '/i/:tripId/calendar':                    'PHASE_1',
  '/i/:tripId/map':                         'PHASE_1',
  '/i/:tripId/settings':                    'PHASE_1',

  // ── PHASE_2: Agency Owner Features ───────────────────────────────────────
  '/agent/admin/dashboard':                 'PHASE_2',
  '/agent/admin/agents':                    'PHASE_2',
  '/agent/admin/agents/:id':                'PHASE_2',
  '/agent/admin/financial':                 'PHASE_2',
  '/agent/admin/white-labeling':            'PHASE_2',
  '/agent/admin/reports':                   'PHASE_2',
  '/agent/admin/activity-log':              'PHASE_2',
  '/agent/admin/attention':                 'PHASE_2',
  '/agent/admin/documents':                 'PHASE_2',
  '/agent/admin/clients':                   'PHASE_2',

  // Advanced CRM — Phase 2
  '/agent/crm/communications/campaigns':    'PHASE_2',
  '/agent/crm/analytics':                   'PHASE_2',
  '/agent/crm/analytics/reports':           'PHASE_2',
  '/agent/crm/automation':                  'PHASE_2',
  '/agent/crm/automation/followup':         'PHASE_2',
  '/agent/crm/settings':                    'PHASE_2',
};

/**
 * Check if a route/feature is available in the current phase.
 *
 * @param {string}           route         Route path to check (supports :param patterns)
 * @param {string|undefined} agencyPhase   Optional per-agency phase override (see isPhaseActive)
 * @returns {boolean}
 */
export const isRouteInPhase = (route, agencyPhase) => {
  // Exact match first (fastest path)
  if (FEATURE_PHASES[route]) {
    return isPhaseActive(FEATURE_PHASES[route], agencyPhase);
  }

  // Pattern match for dynamic routes (e.g. /agent/crm/trips/abc-123)
  for (const [pattern, phase] of Object.entries(FEATURE_PHASES)) {
    const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
    if (regex.test(route)) {
      return isPhaseActive(phase, agencyPhase);
    }
  }

  // Routes not in the map default to available (Phase 0 assumed)
  return true;
};

/**
 * Get the phase key required for a given route.
 *
 * @param {string} route
 * @returns {string|null}
 */
export const getRoutePhase = (route) => {
  if (FEATURE_PHASES[route]) return FEATURE_PHASES[route];

  for (const [pattern, phase] of Object.entries(FEATURE_PHASES)) {
    const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
    if (regex.test(route)) return phase;
  }

  return null;
};

/**
 * Human-readable name for a phase constant.
 *
 * @param {string} phase  Phase constant ('PHASE_0' | 'PHASE_1' | 'PHASE_2')
 * @returns {string}
 */
export const getPhaseName = (phase) => {
  const names = {
    PHASE_0: 'Founding Agent Core',
    PHASE_1: 'Power Agent / CRM Depth',
    PHASE_2: 'Agency Owner Features',
  };
  return names[phase] ?? phase;
};

/**
 * Human-readable name of the next phase after the given one.
 * Returns null if already at the last phase.
 * Consistent with getPhaseName — returns the full descriptive name.
 *
 * @param {string} currentPhase
 * @returns {string|null}
 */
export const getNextPhaseName = (currentPhase) => {
  const next = {
    PHASE_0: 'PHASE_1',
    PHASE_1: 'PHASE_2',
  };
  const nextKey = next[currentPhase];
  return nextKey ? getPhaseName(nextKey) : null;
};
